import React, { useState, useEffect, useContext } from "react";
import axios from "axios";

import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Skeleton,
  Snackbar,
  Alert,
  Fade,
  Paper,
  Divider,
  Chip,
  Tooltip,
} from "@mui/material";

import {
  Search,
  AddCircle,
  Edit,
  Delete,
  School,
  SentimentDissatisfied,
  ChildCare,
  Class,
  Book,
  MenuBookRounded,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import ErrorState from "@componentes/ErrorWindow";
import { api } from "@api/api";
import { CondicaoContext } from "../../../contexto";

import {
  ajustarTamanhoFonteSre,
  tamanhosPaginasAdministrativas,
  tamanhosControlesGlobais,
} from "../../../estilos/global";

// ====================================================
// ESTADO VAZIO
// ====================================================

const EmptyState = ({ navigate, animarEntrada = true }) => (
  <motion.div
    initial={animarEntrada ? { opacity: 0, scale: 0.9 } : false}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "40px 20px",
      height: "60vh",
    }}
  >
    <motion.div
      animate={{
        rotate: [0, 10, -10, 0],
        y: [0, -10, 0],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        repeatType: "reverse",
      }}
    >
      <Box
        sx={{
          width: 140,
          height: 140,
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(63, 81, 181, 0.1) 0%, rgba(63, 81, 181, 0.2) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
          boxShadow: "0 8px 24px rgba(63, 81, 181, 0.1)",
        }}
      >
        <SentimentDissatisfied
          sx={{
            fontSize: 70,
            color: "primary.main",
          }}
        />
      </Box>
    </motion.div>

    <Typography
      variant="h4"
      sx={{
        fontWeight: 700,
        color: "primary.main",
        mb: 1.5,
        background: "linear-gradient(90deg, #3f51b5, #2196f3)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }}
    >
      Nenhuma modalidade encontrada
    </Typography>

    <Typography
      variant="body1"
      sx={{
        color: "text.secondary",
        mb: 4,
        maxWidth: "450px",
        fontSize: ajustarTamanhoFonteSre("1.1rem"),
      }}
    >
      Você ainda não cadastrou nenhuma modalidade educacional. Clique no botão
      abaixo para criar sua primeira modalidade.
    </Typography>

    <Button
      variant="contained"
      startIcon={<AddCircle sx={{ fontSize: "1.5rem" }} />}
      onClick={() => navigate("/admin/adicionar/modalidade")}
      sx={{
        borderRadius: "14px",
        px: 5,
        py: 1.8,
        textTransform: "none",
        fontSize: ajustarTamanhoFonteSre("1.1rem"),
        fontWeight: 600,
        background: "linear-gradient(90deg, #3f51b5, #2196f3)",
        boxShadow: "0 8px 24px rgba(63, 81, 181, 0.3)",
      }}
      component={motion.div}
      whileHover={{
        scale: 1.05,
        boxShadow: "0 12px 28px rgba(63, 81, 181, 0.4)",
      }}
      whileTap={{ scale: 0.98 }}
    >
      Criar Primeira Modalidade
    </Button>
  </motion.div>
);

// ====================================================
// TAG DE MODALIDADE
// ====================================================

const ModalidadeTag = ({ tipo }) => {
  const tagConfig = {
    "Ensino Infantil": {
      icon: <ChildCare sx={{ fontSize: "1rem", mr: 0.5 }} />,
      color: "#ff9800",
      bgColor: "rgba(255, 152, 0, 0.1)",
    },
    "Ensino Fundamental": {
      icon: <Class sx={{ fontSize: "1rem", mr: 0.5 }} />,
      color: "#4caf50",
      bgColor: "rgba(76, 175, 80, 0.1)",
    },
    "Ensino Médio": {
      icon: <Book sx={{ fontSize: "1rem", mr: 0.5 }} />,
      color: "#2196f3",
      bgColor: "rgba(33, 150, 243, 0.1)",
    },
    default: {
      icon: <School sx={{ fontSize: "1rem", mr: 0.5 }} />,
      color: "#9c27b0",
      bgColor: "rgba(156, 39, 176, 0.1)",
    },
  };

  const config = tagConfig[tipo] || tagConfig.default;

  return (
    <motion.div whileHover={{ scale: 1.05 }}>
      <Chip
        icon={config.icon}
        label={tipo}
        sx={{
          borderRadius: "12px",
          px: 1,
          py: 1.5,
          fontSize: ajustarTamanhoFonteSre("0.8rem"),
          fontWeight: 700,
          color: config.color,
          backgroundColor: config.bgColor,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          "& .MuiChip-icon": {
            marginLeft: "4px",
          },
        }}
      />
    </motion.div>
  );
};

// ====================================================
// CARTÃO DE MODALIDADE
// ====================================================

const ModalidadeCard = ({
  modalidade,
  onEdit,
  onDelete,
  animarEntrada = true,
}) => {
  const getTipoModalidade = () => {
    if (modalidade.ensino_infantil) {
      return "Ensino Infantil";
    }

    if (modalidade.nome.includes("Fundamental")) {
      return "Ensino Fundamental";
    }

    if (modalidade.nome.includes("Médio")) {
      return "Ensino Médio";
    }

    return modalidade.nome;
  };

  return (
    <motion.div
      initial={animarEntrada ? { opacity: 0, y: 20 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -5 }}
    >
      <Card
        sx={{
          borderRadius: "18px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          background: "linear-gradient(145deg, #ffffff, #f8faff)",
          border: "1px solid rgba(63, 81, 181, 0.1)",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 12px 40px rgba(63, 81, 181, 0.15)",
            borderColor: "rgba(63, 81, 181, 0.2)",
          },
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Box
              sx={{
                mr: 2,
                width: 50,
                height: 50,
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, rgba(63, 81, 181, 0.1) 0%, rgba(63, 81, 181, 0.2) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(63, 81, 181, 0.1)",
              }}
            >
              <School
                sx={{
                  fontSize: "2rem",
                  color: "primary.main",
                }}
              />
            </Box>

            <Tooltip title={modalidade.nome} arrow>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  flexGrow: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "200px",
                }}
              >
                {modalidade.nome}
              </Typography>
            </Tooltip>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            <ModalidadeTag tipo={getTipoModalidade()} />
          </Box>

          <Divider
            sx={{
              my: 2,
              borderColor: "rgba(0, 0, 0, 0.08)",
              borderBottomWidth: "2px",
              borderRadius: "2px",
            }}
          />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <IconButton
              onClick={() => onEdit(modalidade.id)}
              sx={{
                backgroundColor: "rgba(63, 81, 181, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(63, 81, 181, 0.15)",
                },
              }}
              component={motion.div}
              whileHover={{
                scale: 1.1,
                rotate: 5,
              }}
              whileTap={{ scale: 0.95 }}
            >
              <Edit sx={{ color: "primary.main" }} />
            </IconButton>

            <IconButton
              onClick={() => onDelete(modalidade.id)}
              sx={{
                backgroundColor: "rgba(244, 67, 54, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.15)",
                },
              }}
              component={motion.div}
              whileHover={{
                scale: 1.1,
                rotate: -5,
              }}
              whileTap={{ scale: 0.95 }}
            >
              <Delete sx={{ color: "error.main" }} />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ====================================================
// PÁGINA DE MODALIDADES
// ====================================================

const ModalidadesPage = () => {
  const { role } = useContext(CondicaoContext);
  const tituloSre = role === "superintendent";

  const [modalidades, setModalidades] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();
  const [errorType, setErrorType] = useState(null);

  // ====================================================
  // CONSULTA DAS MODALIDADES
  // ====================================================

  useEffect(() => {
    const fetchModalidades = async () => {
      try {
        const response = await axios.get(api.adminListAllModalities(), {
          withCredentials: true,
        });

        setModalidades(response.data);
      } catch (error) {
        console.error("Erro ao buscar modalidade:", error);

        if (error.message.includes("Network Error")) {
          setErrorType("network");
        } else if (error.response?.status === 403) {
          setErrorType("permission");
        } else {
          setErrorType("module");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchModalidades();
  }, [refreshKey]);

  // ====================================================
  // EXCLUSÃO
  // ====================================================

  const handleDelete = async (id) => {
    try {
      await axios.delete(api.adminDeleteModality(id), {
        withCredentials: true,
      });

      setRefreshKey((prev) => prev + 1);

      setSnackbar({
        open: true,
        message: "Modalidade excluída com sucesso!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erro ao excluir modalidade",
        severity: "error",
      });
    }
  };

  // ====================================================
  // FILTRO
  // ====================================================

  const filteredModalidades = modalidades.filter((modalidade) =>
    modalidade.nome.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ====================================================
  // ERRO
  // ====================================================

  if (errorType) {
    return (
      <ErrorState
        errorType={errorType}
        onRetry={() => {
          setErrorType(null);
          window.location.reload();
        }}
      />
    );
  }

  // ====================================================
  // CARREGAMENTO INICIAL DA SRE
  // A transição ocorrerá após a consulta.
  // ====================================================

  if (tituloSre && loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // ====================================================
  // INTERFACE PRINCIPAL
  // ====================================================

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #f8f9ff 0%, #eef2ff 100%)",
        py: tituloSre ? 0 : { xs: 2, md: 4 },
      }}
    >
      {/* Transição única da página para a SRE. */}

      <motion.div
        onUpdate={() => {}}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{ flex: 1 }}
      >
        <Box
          sx={{
            maxWidth: "100%",
            mx: "auto",
            width: "100%",
            position: "relative",
          }}
        >
          {/* ====================================================
              CABEÇALHO
          ==================================================== */}

          <Box
            display="flex"
            flexDirection={
              tituloSre
                ? { xs: "column", sm: "row" }
                : { xs: "column", md: "row" }
            }
            justifyContent="space-between"
            alignItems={
              tituloSre
                ? { xs: "flex-start", sm: "center" }
                : { xs: "flex-start", md: "center" }
            }
            mb={tituloSre ? 1 : 4}
            gap={tituloSre ? 1.5 : 2}
          >
            <motion.div
              initial={tituloSre ? false : { x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {tituloSre ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.4,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      borderRadius: 1.5,
                      color: "#087cf0",
                      backgroundColor: "#e8f4ff",
                    }}
                  >
                    <MenuBookRounded sx={{ fontSize: 27 }} />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="h1"
                      sx={{
                        color: "#0767d8",
                        fontSize: tamanhosPaginasAdministrativas.tituloPagina,
                        fontWeight: 700,
                        lineHeight: 1.15,
                      }}
                    >
                      Modalidades de Ensino
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.3,
                        color: "#7b8798",
                        fontSize: tamanhosControlesGlobais.descricao,
                        lineHeight: 1.35,
                      }}
                    >
                      Gerencie as etapas e modalidades de ensino disponíveis no
                      sistema.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    background: "linear-gradient(90deg, #3f51b5, #2196f3)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "-0.5px",
                    fontSize: {
                      xs: ajustarTamanhoFonteSre("2rem"),
                      md: ajustarTamanhoFonteSre("2.5rem"),
                    },
                  }}
                >
                  Modalidades de Ensino
                </Typography>
              )}
            </motion.div>

            <motion.div
              initial={tituloSre ? false : { x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{
                duration: 0.5,
                delay: 0.1,
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddCircle sx={{ fontSize: "1.2rem" }} />}
                onClick={() => navigate("/admin/adicionar/modalidade")}
                sx={{
                  borderRadius: "14px",
                  px: 4,
                  py: 1.6,
                  textTransform: "none",
                  fontSize: ajustarTamanhoFonteSre("1rem"),
                  fontWeight: 600,
                  background: "linear-gradient(90deg, #3f51b5, #2196f3)",
                  boxShadow: "0 8px 24px rgba(63, 81, 181, 0.3)",
                }}
                component={motion.div}
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 12px 28px rgba(63, 81, 181, 0.4)",
                }}
                whileTap={{ scale: 0.98 }}
              >
                Adicionar Modalidade
              </Button>
            </motion.div>
          </Box>

          {/* ====================================================
              PESQUISA
          ==================================================== */}

          <motion.div
            initial={tituloSre ? false : { y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.2,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar modalidades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search
                    sx={{
                      mr: 1,
                      color: "primary.main",
                      fontSize: "1.5rem",
                    }}
                  />
                ),
                sx: {
                  borderRadius: "14px",
                  backgroundColor: "background.paper",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                  "&:hover": {
                    boxShadow: "0 6px 20px rgba(63, 81, 181, 0.15)",
                  },
                  height: "56px",
                  fontSize: ajustarTamanhoFonteSre("1.1rem"),
                },
              }}
              sx={{ mb: 4 }}
            />
          </motion.div>

          {/* ====================================================
              LISTA DE MODALIDADES
          ==================================================== */}

          {loading ? (
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={3}
            >
              {[...Array(6)].map((_, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1,
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={200}
                    sx={{
                      borderRadius: "18px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </motion.div>
              ))}
            </Box>
          ) : (
            <AnimatePresence initial={!tituloSre}>
              {filteredModalidades.length > 0 ? (
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap={3}
                >
                  {filteredModalidades.map((modalidade, index) => (
                    <motion.div
                      key={modalidade.id}
                      initial={tituloSre ? false : { opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.05,
                      }}
                      layout={!tituloSre}
                    >
                      <ModalidadeCard
                        modalidade={modalidade}
                        onEdit={(id) =>
                          navigate(`/admin/editar/modalidade/${id}`)
                        }
                        onDelete={handleDelete}
                        animarEntrada={!tituloSre}
                      />
                    </motion.div>
                  ))}
                </Box>
              ) : (
                <Fade in={!loading} appear={!tituloSre}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "18px",
                      p: 4,
                      background: "linear-gradient(145deg, #ffffff, #f8faff)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                      border: "1px solid rgba(63, 81, 181, 0.1)",
                    }}
                  >
                    <EmptyState
                      navigate={navigate}
                      animarEntrada={!tituloSre}
                    />
                  </Paper>
                </Fade>
              )}
            </AnimatePresence>
          )}
        </Box>
      </motion.div>

      {/* ====================================================
          FEEDBACK
      ==================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{
              borderRadius: "14px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
              alignItems: "center",
              fontSize: ajustarTamanhoFonteSre("1rem"),
              fontWeight: 500,
              "& .MuiAlert-icon": {
                fontSize: "1.8rem",
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </motion.div>
      </Snackbar>
    </Box>
  );
};

export default ModalidadesPage;
